import type {
    MicrositeFormData,
    HeroImage,
    GalleryImage,
} from '../pages/CreateMicrosite/types/microsite.types';
import ImageUploadService from '../services/imageUploadService';

/**
 * Transform form data to API payload
 * Handles image uploads and data structure conversion
 */
export class MicrositeTransformService {
    /**
     * Process Hero images with alt text
     * Returns: Array of objects with url and altText
     */
    private static async processHeroImages(
        images: HeroImage[] | undefined,
        storeBrandID: string,
        storeID?: string
    ): Promise<Array<{ url: string; altText: string }>> {
        if (!images || images.length === 0) return [];

        const result: Array<{ url: string; altText: string }> = [];

        for (const image of images) {
            let url: string;

            if (image.url) {
                // Existing URL - keep it
                url = image.url;
            } else if (image.file) {
                // New file - upload it
                const uploadedUrl = await ImageUploadService.uploadSingleFile(
                    image.file,
                    'hero',
                    storeBrandID,
                    storeID
                );
                url = uploadedUrl || '';
            } else {
                continue; // Skip if neither url nor file
            }

            result.push({
                url,
                altText: image.altText || '',
            });
        }

        return result;
    }

    /**
     * Process Gallery images with alt text
     * Returns: Array of objects with url and altText
     */
    private static async processGalleryImages(
        images: GalleryImage[] | undefined,
        storeBrandID: string,
        storeID?: string
    ): Promise<Array<{ url: string; altText: string }>> {
        if (!images || images.length === 0) return [];

        const result: Array<{ url: string; altText: string }> = [];

        for (const image of images) {
            let url: string;

            if (image.url) {
                // Existing URL - keep it
                url = image.url;
            } else if (image.file) {
                // New file - upload it
                const uploadedUrl = await ImageUploadService.uploadSingleFile(
                    image.file,
                    'gallery',
                    storeBrandID,
                    storeID
                );
                url = uploadedUrl || '';
            } else {
                continue; // Skip if neither url nor file
            }

            result.push({
                url,
                altText: image.altText || '',
            });
        }

        return result;
    }

    /**
     * Process a single image field
     */
    private static async processSingleImage(
        image: File | string | undefined,
        section: 'logo' | 'ogImage' | 'modal',
        storeBrandID: string,
        storeID?: string
    ): Promise<string | undefined> {
        if (!image) return undefined;

        if (typeof image === 'string') {
            return image; // Already uploaded
        }

        // Upload new file
        return await ImageUploadService.uploadSingleFile(image, section, storeBrandID, storeID);
    }

    /**
     * Transform form data to API payload
     * This is the main function that converts the form state to the API request body
     */
    static async transformToApiPayload(
        formData: MicrositeFormData,
        action: 'saveDraft' | 'publish'
    ): Promise<any> {
        const storeBrandID = formData.storeBrandID;
        const storeID = formData.storeID;

        if (!storeBrandID) {
            throw new Error('Store Brand ID is required');
        }

        // Upload all images in parallel
        const [logo, ogImage, heroImageUrls, galleryImageUrls] = await Promise.all([
            // Logo
            this.processSingleImage(formData.logo, 'logo', storeBrandID, storeID),
            // OG Image
            this.processSingleImage(formData.metaData?.ogImage, 'ogImage', storeBrandID, storeID),
            // Hero Images
            this.processHeroImages(formData.hero?.imageUrls, storeBrandID, storeID),
            // Gallery Images
            this.processGalleryImages(formData.gallery?.imageUrls, storeBrandID, storeID),
        ]);

        // Construct the API payload
        const payload: any = {
            micrositeID: formData.micrositeID, // Include for updates, omit for new microsites
            storeBrandID,
            storeBrandSlug: formData.storeBrandSlug,
            action,
            micrositeData: {
                tagline: formData.tagline,
                logo,
                metaData: formData.metaData
                    ? {
                          title: formData.metaData.title,
                          description: formData.metaData.description,
                          keywords: formData.metaData.keywords?.length
                              ? [formData.metaData.keywords.join(', ')]
                              : undefined,
                          ogImage,
                      }
                    : undefined,

                // Hero Section - Images and Tags
                hero:
                    formData.hero && (formData.hero.imageUrls || formData.hero.tags)
                        ? {
                              imageUrls: heroImageUrls,
                              tags: formData.hero.tags || [],
                          }
                        : undefined,

                // Hero Banner - Text content and CTA
                heroBanner: formData.heroBanner
                    ? {
                          title: formData.heroBanner.title,
                          subtitle: formData.heroBanner.subtitle,
                          highlightLabel: formData.heroBanner.highlightLabel,
                          ctaText: formData.heroBanner.ctaText,
                          redirectionUrl: formData.heroBanner.redirectionUrl,
                      }
                    : undefined,

                // Modal
                modal: formData.modal
                    ? {
                          headerBadge: formData.modal.headerBadge,
                          headerTitle: formData.modal.headerTitle,
                          headerSubtitle: formData.modal.headerSubtitle,
                          primaryButtonText: formData.modal.primaryButtonText,
                          primaryButtonInfo: formData.modal.primaryButtonInfo,
                          footerNote: formData.modal.footerNote,
                      }
                    : undefined,

                // Location
                location: formData.location
                    ? {
                          holidayInfo: formData.location.holidayInfo,
                      }
                    : undefined,

                // Gallery
                gallery:
                    galleryImageUrls.length > 0
                        ? {
                              imageUrls: galleryImageUrls,
                          }
                        : undefined,

                // Deals
                deals:
                    formData.deals?.items && formData.deals.items.length > 0
                        ? {
                              items: formData.deals.items.map((deal) => ({
                                  giftItem: deal.giftItem,
                                  limitedOffer: deal.limitedOffer,
                                  spendAmount: deal.spendAmount,
                                  validity: deal.validity?.toISOString(),
                              })),
                          }
                        : undefined,

                // Social
                social: formData.social
                    ? {
                          facebook: formData.social.facebook
                              ? {
                                    username: formData.social.facebook.username,
                                    followers: formData.social.facebook.followers,
                                }
                              : undefined,
                          instagram: formData.social.instagram
                              ? {
                                    username: formData.social.instagram.username,
                                    followers: formData.social.instagram.followers,
                                }
                              : undefined,
                      }
                    : undefined,

                // Footer
                footer: formData.footer
                    ? {
                          licenseNumber: formData.footer.licenseNumber,
                          minimumLegalDrinkingAge: formData.footer.minimumLegalDrinkingAge,
                      }
                    : undefined,

                // GeoLocation
                geoLocation:
                    formData.geoLocation &&
                    formData.geoLocation.latitude &&
                    formData.geoLocation.longitude
                        ? {
                              type: 'Point',
                              coordinates: [
                                  Number(formData.geoLocation.longitude),
                                  Number(formData.geoLocation.latitude),
                              ],
                              validity: formData.geoLocation.validity?.toISOString(),
                          }
                        : undefined,
            },
        };

        // Add store-specific fields if this is a store microsite
        if (storeID) {
            payload.storeID = storeID;
            payload.storeSlug = formData.storeSlug;

            // Store operations config
            if (formData.storeOperationsConfig) {
                payload.storeOperationsConfig = {
                    timings: formData.storeOperationsConfig.timings?.map((timing) => ({
                        day: timing.day,
                        openingTime: timing.openingTime.toISOString(),
                        closingTime: timing.closingTime.toISOString(),
                    })),
                    phone: formData.storeOperationsConfig.phone,
                    whatsappNumber: formData.storeOperationsConfig.whatsappNumber,
                    location:
                        formData.geoLocation?.latitude && formData.geoLocation?.longitude
                            ? {
                                  type: 'Point',
                                  coordinates: [
                                      Number(formData.geoLocation.longitude),
                                      Number(formData.geoLocation.latitude),
                                  ],
                              }
                            : undefined,
                };
            }
        }

        // Brand operations config
        if (formData.storeBrandOperationsConfig) {
            payload.storeBrandOperationsConfig = {
                phone: formData.storeBrandOperationsConfig.phone,
                whatsappNumber: formData.storeBrandOperationsConfig.whatsappNumber,
            };
        }

        return payload;
    }
}

export default MicrositeTransformService;
